import React, {useCallback, useMemo, useState} from "react";
import {
    Box,
    Center,
    Group,
    Loader,
    LoadingOverlay,
    Stack,
    Text,
} from "@mantine/core";
import {useAppSelector} from "../../../../app/hooks";
import {chatApi} from "../../chatApi";
import {
    selectCompletedBackgroundStreams,
    selectStreamingThreadIds,
    threadsSelectors,
} from "../../threadsSlice";
import {ThreadButton} from "./ThreadButton";
import {
    getLangCodeFromEnum,
    ThreadInfo,
    USER_THREADS_PAGE_SIZE,
} from "../../../../types";
import scrollClasses from "../../components/scrollContainer.module.css";
import {useMediaQuery} from "@mantine/hooks";
import {useTranslation} from "react-i18next";
import {bffApi} from "../../bffApi";
import {useParams} from "react-router-dom";
import {parseApiDate} from "../../../../utils/dateUtils";

const ChatThreads: React.FC<{toggleNavBar: () => void}> = ({toggleNavBar}) => {
    const streamingThreadIds = useAppSelector(selectStreamingThreadIds);
    const completedBackgroundStreamIds = useAppSelector(
        selectCompletedBackgroundStreams
    );
    const allThreadEntities = useAppSelector(threadsSelectors.selectAll);
    const completedRealThreadIds = useMemo(
        () =>
            completedBackgroundStreamIds
                .map(
                    (eid) =>
                        allThreadEntities.find((e) => e.id === eid)?.realThreadId
                )
                .filter((x): x is string => !!x),
        [completedBackgroundStreamIds, allThreadEntities]
    );
    const {threadId: selectedThreadId} = useParams<{threadId: string}>();

    const isDesktop = useMediaQuery("(min-width: 48em)");
    const {t} = useTranslation();
    const userData = bffApi.endpoints.getUser.useQuery();
    const lang = getLangCodeFromEnum(userData.data?.spraakId);
    const [page, setPage] = useState(0);
    const {data, isLoading, isFetching, fulfilledTimeStamp} =
        chatApi.endpoints.getUserThreads.useQuery({
            page,
            pageSize: USER_THREADS_PAGE_SIZE,
        });

    const handleScroll = useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            if (isFetching) return;
            const {scrollHeight, scrollTop, clientHeight} = event.currentTarget;
            if (scrollHeight - scrollTop - clientHeight <= 1) {
                if (data?.totalPages && page < data.totalPages - 1) {
                    setPage(page + 1);
                }
            }
        },
        [data?.totalPages, page, isFetching, fulfilledTimeStamp]
    );

    const {groupedThreads, sortedThreads} = useMemo(() => {
        const localByThreadId = new Map<string, ThreadInfo>();
        for (const entity of allThreadEntities) {
            const placeholderId = entity.realThreadId ?? entity.id;
            const firstUserMessage = entity.messages.find(
                (m) => m.role === "user"
            );
            localByThreadId.set(placeholderId, {
                threadId: placeholderId,
                name:
                    entity.name ??
                    firstUserMessage?.content ??
                    t("common.defaultThreadTitle"),
                lastUpdated:
                    entity.lastUpdated ?? new Date().toISOString(),
                messageCount: entity.messages.length,
            });
        }

        // Server list, with local entities overriding matching entries.
        // For lastUpdated take whichever side is newer — the server cache
        // gets optimistically bumped on send, the local entity is bumped
        // when streams complete, and we want the sidebar to reflect the
        // most recent activity regardless of which one changed.
        const combined: ThreadInfo[] = (data?.items ?? []).map((item) => {
            const local = localByThreadId.get(item.threadId);
            if (!local) return item;
            const localTs = parseApiDate(local.lastUpdated).getTime();
            const serverTs = parseApiDate(item.lastUpdated).getTime();
            return {
                ...local,
                lastUpdated:
                    serverTs > localTs ? item.lastUpdated : local.lastUpdated,
            };
        });
        const serverThreadIds = new Set(
            (data?.items ?? []).map((i) => i.threadId)
        );
        // Prepend local entities that aren't in the server list yet
        for (const [id, local] of localByThreadId) {
            if (!serverThreadIds.has(id)) {
                combined.unshift(local);
            }
        }

        const sortedThreads = combined.sort(
            (a, b) =>
                parseApiDate(b.lastUpdated).getTime() -
                parseApiDate(a.lastUpdated).getTime()
        );

        return {
            sortedThreads,
            groupedThreads:
                groupThreadsByDate(sortedThreads, {
                    language: lang,
                    today: t("date.today"),
                    last30Days: t("date.last30Days"),
                    previously: t("date.previously"),
                }) || {},
        };
    }, [
        data,
        allThreadEntities,
        lang,
        t,
        fulfilledTimeStamp,
    ]);

    const handleThreadClick = useCallback(() => {
        if (!isDesktop) {
            toggleNavBar();
        }
    }, [isDesktop, toggleNavBar]);

    return (
        <Box
            style={{
                display: "flex",
                flex: 1,
                flexDirection: "column",
                overflow: "hidden",
                position: "relative",
            }}
        >
            <Box
                className={scrollClasses.scrollboxDark}
                py={isDesktop ? "md" : "sm"}
                px="xs"
                style={{
                    flex: 1,
                    overflowY: "auto",
                    position: "relative",
                }}
                onScroll={handleScroll}
            >
                {Object.entries(groupedThreads).map(
                    ([groupLabel, group], index, array) => (
                        <Stack key={groupLabel} mb="xl" gap="xs" w="100%">
                            <Group justify="space-between">
                                <Text fz="xs" fw={700} ml="xs">
                                    {groupLabel}
                                </Text>
                            </Group>
                            {group.map((thread) => (
                                <ThreadButton
                                    key={thread.threadId}
                                    id={thread.threadId}
                                    name={thread.name}
                                    onClick={handleThreadClick}
                                    isSelected={
                                        thread.threadId === selectedThreadId
                                    }
                                    isLoading={streamingThreadIds.includes(
                                        thread.threadId
                                    )}
                                    isCompleted={completedRealThreadIds.includes(
                                        thread.threadId
                                    )}
                                />
                            ))}
                            {index === array.length - 1 &&
                                !isLoading &&
                                isFetching && (
                                    <Center>
                                        <Loader
                                            type="dots"
                                            color="white"
                                            size="sm"
                                        />
                                    </Center>
                                )}
                        </Stack>
                    )
                )}
                {sortedThreads.length === 0 && (
                    <Center>
                        <Text fz="xs" my="xl" ml="sm">
                            {t("common.getStarted")}
                        </Text>
                    </Center>
                )}
                <LoadingOverlay
                    visible={isLoading}
                    loaderProps={{
                        type: "dots",
                        color: "white",
                        size: "sm",
                    }}
                    overlayProps={{
                        color: "var(--dib-brand-base)",
                        backgroundOpacity: 1,
                    }}
                />
            </Box>
        </Box>
    );
};

const groupThreadsByDate = (
    threads: ThreadInfo[],
    {
        language,
        today: todayString,
        last30Days: last30DaysString,
        previously: previouslyString,
    }: {
        language: string;
        today: string;
        last30Days: string;
        previously: string;
    }
) => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const grouped: Record<string, ThreadInfo[]> = {};

    threads.forEach((thread) => {
        const threadDate = parseApiDate(thread.lastUpdated);
        if (Number.isNaN(threadDate.getTime()) || threadDate.getTime() <= 0) {
            if (!grouped[previouslyString]) grouped[previouslyString] = [];
            grouped[previouslyString].push(thread);
            return;
        }
        const isToday = threadDate.toDateString() === today.toDateString();
        const isWithinLast30Days = threadDate >= thirtyDaysAgo && !isToday;

        if (isToday) {
            if (!grouped[todayString]) grouped[todayString] = [];
            grouped[todayString].push(thread);
            return;
        }

        if (isWithinLast30Days) {
            if (!grouped[last30DaysString]) grouped[last30DaysString] = [];
            grouped[last30DaysString].push(thread);
            return;
        }

        // Group by month (Norwegian locale)
        const month = threadDate.toLocaleString(language, {
            month: "long",
            year: "numeric",
        });
        if (!grouped[month]) grouped[month] = [];
        grouped[month].push(thread);
    });

    return grouped;
};

export default ChatThreads;
